import React, { useEffect, useState } from 'react';
import { Field, useFormikContext } from 'formik';
import {
    FormControl,
    FormHelperText,
    InputLabel,
    MenuItem,
    Select,
    SelectChangeEvent,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import { Info } from '@mui/icons-material';
import { ComboBox, InfoText, PhoneInputField, UCNField } from '@fnol/library/src/components';
import {
    GenericObject,
    getApplicationId,
    getBirthDateFromUCN,
    getIsSalesForce,
    getIsWebsite,
    hasValueAndIsSalesForce,
} from '@fnol/library/src/lib';
import {
    legalPersonType,
    personType,
    translations,
    nameMaxLength,
    surnameMaxLength,
    ucnMaxLength,
    question50MaxLength,
    questionMaxLength,
    constValues,
} from '../../lib/consts';
import { getContactData } from '../../rpc/actions';

type IndividualProps = {
    individualType: personType;
    id?: number;
};

const Individual = ({ individualType, id }: IndividualProps) => {
    const { values, errors, touched, handleChange, setFieldValue } = useFormikContext<GenericObject>();

    const [appropriateRoles, setAppropriateRoles] = useState([]);
    const [selectedRoles, setselectedRoles] = useState();

    const role = `${personType[individualType]}IndividualPersonRole`;
    const name = `${personType[individualType]}IndividualName`;
    const lastname = `${personType[individualType]}IndividualLastName`;
    const dateOfBirth = `${personType[individualType]}IndividualDateOfBirth`;
    const ucn = `${personType[individualType]}IndividualUCN`;
    const country = `${personType[individualType]}IndividualCountry`;
    const countryText = `${personType[individualType]}IndividualMyCountry`;
    const city = `${personType[individualType]}IndividualCity`;
    const cityInSerbia = `${personType[individualType]}IndividualCityInSerbia`;
    const street = `${personType[individualType]}IndividualStreet`;
    const phone = `${personType[individualType]}IndividualPhone`;
    const email = `${personType[individualType]}IndividualEmail`;
    const streetNumber = `${personType[individualType]}IndividualStreetNumber`;

    const isIndividualSubmitterOnWebsite =
        values.jbkProdukcija === true &&
        individualType === personType.submitter &&
        values.submitterType === legalPersonType.individual &&
        getIsWebsite(getApplicationId());

    const ucnOnBlur = async (e: React.FocusEvent<HTMLTextAreaElement | HTMLInputElement>) => {
        checkIfUCNExists(e);
        const birthDate = getBirthDateFromUCN(e.target?.value);
        if (!!birthDate) setFieldValue(dateOfBirth, birthDate);

        //if it is not via website, we shall check if contact data exists for this UCN and if it does,
        //  we shall prefill the mobile and email fields with that data.
        //  This is only for Serbia, as UCN is only valid for Serbia.
        if (!getIsWebsite(getApplicationId()) && personType[individualType] === 'submitter') {
            const contactData = await getContactData(e.target.value);
            if (contactData.data) {
                if (contactData.data.telefon && contactData.data.telefon !== '') {
                    setFieldValue(phone, contactData.data.telefon);
                }
                if (contactData.data.email && contactData.data.email !== '') {
                    setFieldValue(email, contactData.data.email);
                }
            }
        }
    };

    const checkIfUCNExists = (e: React.FocusEvent<HTMLTextAreaElement | HTMLInputElement>) => {
        const insertTypes: number[] = [personType.submitter];
        const insertedUcns: GenericObject = {};

        insertTypes.map((insertType) => {
            if (insertType !== individualType) {
                // skip current type of person (only one person can be entered)
                insertedUcns[`${personType[insertType]}IndividualUCN`] =
                    values[`${personType[insertType]}IndividualUCN`];
            }
        });

        let duplicatedUcnExists = false;

        for (const [key, value] of Object.entries(insertedUcns)) {
            if (e.target?.value && e.target?.value !== '' && value === e.target?.value) {
                duplicatedUcnExists = true;
            }
        }
    };

    // selected country textual representation
    const selectedIndividualCountry = values.preparedCountries?.find(
        (item: GenericObject) => item.id === parseInt(values[country] as string),
    );

    values[countryText] = selectedIndividualCountry?.text;

    const handleChangeRole = (event: SelectChangeEvent<HTMLSelectElement>) => {
        const selectedRole = values.roles.find((item: GenericObject) => item.id === event.target.value);
        setFieldValue(role, selectedRole);
        setselectedRoles(selectedRole['id']);
    };

    useEffect(() => {
        const individualRoles = values.roles.filter((item: GenericObject) => item.individual);
        setAppropriateRoles(individualRoles);
        if (values[role] && values[role].id) {
            setselectedRoles(values[role].id);
        }
    }, []);

    return (
        <>
            <FormControl>
                <InputLabel id={`${role}-label`}>{translations.LEGAL_ENTITY_PERSON_ROLE}</InputLabel>

                <Select
                    id={role}
                    labelId={`${role}-label`}
                    name={role}
                    value={selectedRoles || ''}
                    label={translations.LEGAL_ENTITY_PERSON_ROLE}
                    error={!!(touched[role] && errors[role])}
                    onChange={handleChangeRole}
                >
                    {appropriateRoles.map((option: any) => {
                        return (
                            <MenuItem key={option.id} value={option.id}>
                                {option.value}
                            </MenuItem>
                        );
                    })}
                </Select>

                {touched[role] && errors[role] ? (
                    <FormHelperText error={true}>
                        <>{errors[role]}</>
                    </FormHelperText>
                ) : null}
            </FormControl>
            <FormControl>
                <Field
                    name={name}
                    label={translations.personalInfo.NAME}
                    as={TextField}
                    inputProps={{
                        maxLength: nameMaxLength,
                    }}
                    error={!!(touched[name] && errors[name])}
                    required
                />
                {touched[name] && errors[name] ? (
                    <FormHelperText error={true}>
                        <>{errors[name]}</>
                    </FormHelperText>
                ) : null}
            </FormControl>

            <FormControl>
                <Field
                    name={lastname}
                    label={translations.personalInfo.LAST_NAME}
                    as={TextField}
                    inputProps={{
                        maxLength: surnameMaxLength,
                    }}
                    error={!!(touched[lastname] && errors[lastname])}
                    required
                />
                {touched[lastname] && errors[lastname] ? (
                    <FormHelperText error={true}>
                        <>{errors[lastname]}</>
                    </FormHelperText>
                ) : null}
            </FormControl>

            {values[country] === constValues.countrySerbiaId && (
                <FormControl>
                    <Field
                        name={ucn}
                        label={translations.personalInfo.UCN}
                        as={UCNField}
                        inputProps={{
                            maxLength: ucnMaxLength,
                        }}
                        onBlur={ucnOnBlur}
                        error={!!(touched[ucn] && errors[ucn])}
                        required
                    />
                    {touched[ucn] && errors[ucn] ? (
                        <FormHelperText error={true}>
                            <>{errors[ucn]}</>
                        </FormHelperText>
                    ) : null}
                </FormControl>
            )}

            {values[country] !== constValues.countrySerbiaId.toString(10) &&
            values[country] !== constValues.countrySerbiaId ? (
                <FormControl>
                    <Field
                        name={city}
                        label={translations.CITY}
                        as={TextField}
                        inputProps={{
                            maxLength: question50MaxLength,
                        }}
                        error={!!(touched[city] && errors[city])}
                        required
                    />
                    {touched[city] && errors[city] ? (
                        <FormHelperText error={true}>
                            <>{errors[city]}</>
                        </FormHelperText>
                    ) : null}
                </FormControl>
            ) : (
                <FormControl>
                    <Field
                        name={cityInSerbia}
                        label={translations.CITY}
                        options={
                            values.cities || [
                                {
                                    id: '',
                                    value: '',
                                    text: '',
                                    code: '',
                                    zipCode: '',
                                },
                            ]
                        }
                        onChange={handleChange}
                        defaultValue={values[cityInSerbia]}
                        as={ComboBox}
                        inputProps={{
                            maxLength: question50MaxLength,
                        }}
                        error={!!(touched[cityInSerbia] && errors[cityInSerbia])}
                        required
                    />
                    {touched[cityInSerbia] && errors[cityInSerbia] ? (
                        <FormHelperText error={true}>
                            <>{errors[cityInSerbia]}</>
                        </FormHelperText>
                    ) : null}
                </FormControl>
            )}

            <FormControl>
                <Field
                    name={street}
                    label={translations.STREET}
                    as={TextField}
                    inputProps={{
                        maxLength: question50MaxLength,
                    }}
                    error={!!(touched[street] && errors[street])}
                    required
                />
                {touched[street] && errors[street] ? (
                    <FormHelperText error={true}>
                        <>{errors[street]}</>
                    </FormHelperText>
                ) : null}
            </FormControl>

            <FormControl>
                <Field
                    name={streetNumber}
                    label={translations.STREET_NUMBER}
                    as={TextField}
                    inputProps={{
                        maxLength: question50MaxLength,
                    }}
                    error={!!(touched[streetNumber] && errors[streetNumber])}
                    required
                />
                {touched[streetNumber] && errors[streetNumber] ? (
                    <FormHelperText error={true}>
                        <>{errors[streetNumber]}</>
                    </FormHelperText>
                ) : null}
            </FormControl>

            {getIsSalesForce(getApplicationId()) && (
                <Stack direction="row" alignItems="top" spacing={2} marginTop={2} marginBottom={3}>
                    <Info color="primary" />
                    <Typography variant="body2" paddingTop={0.5}>
                        {translations.validationMessages.CONTACT_REQUIRED}
                    </Typography>
                </Stack>
            )}

            <FormControl>
                <PhoneInputField
                    name={phone}
                    specialLabel={translations.personalInfo.MOBILE}
                    country={'rs'}
                    required={!hasValueAndIsSalesForce(values[email])}
                />
            </FormControl>

            <FormControl>
                <Field
                    name={email}
                    label={translations.personalInfo.EMAIL}
                    type="email"
                    as={TextField}
                    inputProps={{
                        maxLength: questionMaxLength,
                    }}
                    error={!!(touched[email] && errors[email])}
                    required={!hasValueAndIsSalesForce(values[phone])}
                />
                {touched[email] && errors[email] ? (
                    <FormHelperText error={true}>
                        <>{errors[email]}</>
                    </FormHelperText>
                ) : null}
            </FormControl>
            {isIndividualSubmitterOnWebsite && <InfoText label={translations.INDIVIDUAL_CONTACT_INFO_VERIFICATION} />}
        </>
    );
};

export const IndividualCleanup = (
    type: personType,
    setFieldValue: (field: string, value: string | number | null, shouldValidate?: boolean | undefined) => void,
    userId?: number,
) => {
    const idUser = userId ? userId : '';
    setFieldValue(`${personType[type]}${idUser}IndividualPersonRole`, null);
    setFieldValue(`${personType[type]}${idUser}IndividualName`, '');
    setFieldValue(`${personType[type]}${idUser}IndividualLastName`, '');
    setFieldValue(`${personType[type]}${idUser}IndividualDateOfBirth`, '');
    setFieldValue(`${personType[type]}${idUser}IndividualUCN`, '');
    setFieldValue(`${personType[type]}${idUser}IndividualEmail`, '');
    setFieldValue(`${personType[type]}${idUser}IndividualPhone`, constValues.dialCodeSerbia);
    setFieldValue(`${personType[type]}${idUser}IndividualCountry`, constValues.countrySerbiaId);
    setFieldValue(`${personType[type]}${idUser}IndividualStreet`, '');
    setFieldValue(`${personType[type]}${idUser}IndividualStreetNumber`, '');
    setFieldValue(`${personType[type]}${idUser}IndividualCity`, '');
    setFieldValue(`${personType[type]}${idUser}IndividualCityInSerbia`, '');
};

export default Individual;
